import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  FranchiseInfoFragment,
  FullFranchiseAccessFragment,
  FullSalonAccessCoreFragment,
  Salon,
  SalonContactInfo,
  UserSalonRoleType,
} from "@/gql/graphql";
import { AuthEvent, authEventEmitter } from "@/lib/authorization/auth-events";
import { TimezoneService } from "@/lib/services/timezone-service";
import { useConfigStore } from "./use-config-store";

// Re-export for convenience
export type {
  FullFranchiseAccessFragment,
  FullSalonAccessCoreFragment,
  FranchiseInfoFragment,
  Salon,
  SalonContactInfo,
  UserSalonRoleType as UserSalonRole,
};

// Create enum-like object for SalonContactType
export const SalonContactType = {
  EMAIL: "EMAIL" as const,
  PHONE: "PHONE" as const,
} as const;

export type SalonContactType = (typeof SalonContactType)[keyof typeof SalonContactType];

interface SalonState {
  // Core access data - single source of truth from backend
  franchiseAccess: FullFranchiseAccessFragment[];

  // Current selection
  currentSalon: FullSalonAccessCoreFragment | null;

  // UI state
  isLoading: boolean;
  hasNoSalonAccess: boolean;

  // Core actions
  setFranchiseAccess: (access: FullFranchiseAccessFragment[]) => void;
  setCurrentSalon: (salon: FullSalonAccessCoreFragment | null) => void;
  setLoading: (loading: boolean) => void;
  setNoSalonAccess: (noAccess: boolean) => void;
  clearSalons: () => void;

  // Computed properties
  getCurrentFranchise: () => FullFranchiseAccessFragment | null;

  // Utility functions
  getSalonsForFranchise: (franchiseId: string) => FullSalonAccessCoreFragment[];
  getFranchiseRole: (franchiseId: string) => string | null;
  getSalonRole: (salonId: string) => string | null;
}

export const useSalonId = () => {
  const salon = useSalonStore((state) => state.currentSalon);

  if (!salon) {
    throw new Error("Salon is not available");
  }

  return salon.id;
};

export const useSalonStore = create<SalonState>()(
  persist(
    (set, get) => ({
      // Initial state
      franchiseAccess: [],
      currentSalon: null,
      isLoading: false,
      hasNoSalonAccess: false,

      // Core actions
      setFranchiseAccess: (access: FullFranchiseAccessFragment[]) => {
        const totalSalons = access.flatMap((fa) => fa.franchise.salons).length;
        const currentSalon = get().currentSalon;

        // Validate that current salon still exists in the new access list
        let validatedCurrentSalon = currentSalon;
        if (currentSalon) {
          const salonStillExists = access.some((fa) =>
            fa.franchise.salons.some((s) => s.id === currentSalon.id)
          );

          if (!salonStillExists) {
            // Current salon no longer in access list, clear it
            validatedCurrentSalon = null;

            // Clear configuration data for removed salon
            const { clearSalon } = useConfigStore.getState();
            clearSalon(currentSalon.id);
          }
        }

        // Auto-select salon if no current salon and exactly one salon available
        if (!validatedCurrentSalon && totalSalons === 1 && access.length > 0) {
          const firstFranchise = access[0];
          if (firstFranchise.franchise.salons.length === 1) {
            const salon = firstFranchise.franchise.salons[0] as FullSalonAccessCoreFragment;
            validatedCurrentSalon = salon;

            // Set timezone for auto-selected salon
            try {
              TimezoneService.setSalonTimezone(salon.timezone, salon.id);
            } catch (error) {
              console.warn(`Failed to set salon timezone for ${salon.name}:`, error);
            }
          }
        }

        set({
          franchiseAccess: access,
          currentSalon: validatedCurrentSalon,
          hasNoSalonAccess: totalSalons === 0,
          isLoading: false,
        });
      },

      setCurrentSalon: (salon: FullSalonAccessCoreFragment | null) => {
        if (salon) {
          // Set the salon timezone globally using TimezoneService
          try {
            TimezoneService.setSalonTimezone(salon.timezone, salon.id);
          } catch (error) {
            console.warn(`Failed to set salon timezone for ${salon.name}:`, error);
          }

          // Clear configuration data for previous salon if switching
          const currentSalon = get().currentSalon;
          if (currentSalon && currentSalon.id !== salon.id) {
            const { clearSalon } = useConfigStore.getState();
            clearSalon(currentSalon.id);

            // Emit account switch event for side effects (Firebase messaging, analytics, etc.)
            authEventEmitter.emit(AuthEvent.ACCOUNT_SWITCHED);
          }
        }

        set({ currentSalon: salon });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setNoSalonAccess: (noAccess: boolean) => {
        set({ hasNoSalonAccess: noAccess });
      },

      clearSalons: () => {
        // Clear configuration data for current salon
        const currentSalon = get().currentSalon;
        if (currentSalon) {
          const { clearSalon } = useConfigStore.getState();
          clearSalon(currentSalon.id);
        }

        set({
          franchiseAccess: [],
          currentSalon: null,
          isLoading: false,
          hasNoSalonAccess: false,
        });
      },

      // Computed properties
      getCurrentFranchise: () => {
        const { franchiseAccess, currentSalon } = get();
        if (!currentSalon) return null;

        return (
          franchiseAccess.find((fa) =>
            fa.franchise.salons.some((salon) => salon.id === currentSalon.id)
          ) || null
        );
      },

      // Utility functions
      getSalonsForFranchise: (franchiseId: string) => {
        const { franchiseAccess } = get();
        const access = franchiseAccess.find((fa) => fa.franchiseId === franchiseId);
        return access?.franchise.salons || [];
      },

      getFranchiseRole: (franchiseId: string) => {
        const { franchiseAccess } = get();
        const access = franchiseAccess.find((fa) => fa.franchiseId === franchiseId);
        return access?.role || null;
      },

      getSalonRole: (salonId: string) => {
        const { franchiseAccess } = get();
        for (const access of franchiseAccess) {
          const salon = access.franchise.salons.find((s) => s.id === salonId);
          if (salon) {
            return salon.role;
          }
        }
        return null;
      },
    }),
    {
      name: "salon-storage",
      partialize: (state) => ({
        franchiseAccess: state.franchiseAccess,
        currentSalon: state.currentSalon,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          // Set the salon timezone globally when state is restored from localStorage
          if (state?.currentSalon) {
            try {
              TimezoneService.setSalonTimezone(state.currentSalon.timezone, state.currentSalon.id);
            } catch (error) {
              console.warn(
                `Failed to restore salon timezone for ${state.currentSalon.name}:`,
                error
              );
            }
          }
        };
      },
    }
  )
);
